import { FolderIcon } from '@heroicons/react/24/outline';
import { memo, useCallback, useMemo } from 'react';
import { ContextMenuSubmenu } from '../../../../components/ContextMenu/ContextMenuSubmenu';
import { SelectItemContent } from '../../../../components/selects/SelectItem';
import { type AnnotationContextGroup, isAnnotationContextGroup } from '../../../../contexts/AnnotationContext';
import { useAnnotationTree, useAnnotationLookup } from '../../../../hooks/potree/useAnnotationContext';
import { useGroupOperations } from '../../../../hooks/potree/useGroupOperations';
import { T } from '../../../../translation/src';
import { GroupType } from '../../../../types/graphqlTypes';
import { GROUP_ICONS_BY_TYPE } from '../../../../utils/annotationIcons';
import { resolveFloorGroupIdentifier } from '../../../../utils/roomFolders';

interface MoveToGroupSubmenuProps {
  annotationIdentifiers: string[];
  ignoreGroupIdentifier?: string | null;
}

/** Sketch annotation types that may live inside a ROOM folder. */
const FOLDERABLE_TYPES = new Set(['SKETCH_OBJECT', 'OPENING_SKETCH']);

const MoveToGroupSubmenu_: React.FC<MoveToGroupSubmenuProps> = ({ annotationIdentifiers, ignoreGroupIdentifier }) => {
  const annotations = useAnnotationTree();
  const lookup = useAnnotationLookup();
  const { moveAnnotationsToGroup } = useGroupOperations();

  const groups = useMemo(() => {
    // Floors are never move targets; DEFAULT/SURFACE groups always are.
    const result: AnnotationContextGroup[] = [];
    const selected = annotationIdentifiers.map((id) => lookup[id]).filter(Boolean);
    const allSketches = selected.length > 0 && selected.every((a) => FOLDERABLE_TYPES.has(a.type));
    // The floors the selection lives in (a sketch's group is its floor or a
    // room folder of that floor) — folders are offered same-floor only.
    const groupsById = new Map<string, AnnotationContextGroup>();
    for (const item of annotations) {
      if (!isAnnotationContextGroup(item)) continue;
      groupsById.set(item.identifier, item);
      for (const child of item.annotations) {
        if (isAnnotationContextGroup(child)) groupsById.set(child.identifier, child);
      }
    }
    const flatGroups = [...groupsById.values()].map((g) => ({ identifier: g.identifier, parentIdentifier: g.parentIdentifier ?? null }));
    const selectionFloorIds = new Set(
      selected
        .map((a) => {
          const resolved = resolveFloorGroupIdentifier(flatGroups, a.groupIdentifier);
          // Floor-strict on top of the canonical resolver: a sketch sitting
          // in a plain DEFAULT group resolves to that group, not a floor.
          return resolved && groupsById.get(resolved)?.type === GroupType.Floor ? resolved : undefined;
        })
        .filter((id): id is string => !!id),
    );

    for (const item of annotations) {
      if (!isAnnotationContextGroup(item) || item.identifier === ignoreGroupIdentifier) continue;
      if (item.type === GroupType.Floor) {
        if (!allSketches) continue;
        for (const child of item.annotations) {
          if (isAnnotationContextGroup(child) && child.type === GroupType.Room && child.identifier !== ignoreGroupIdentifier && selectionFloorIds.has(item.identifier)) {
            result.push(child);
          }
        }
        continue;
      }
      result.push(item);
    }
    return result;
  }, [annotations, ignoreGroupIdentifier, annotationIdentifiers, lookup]);

  const onMoveToGroup = useCallback(
    (groupIdentifier: string) => {
      moveAnnotationsToGroup(annotationIdentifiers, groupIdentifier);
    },
    [moveAnnotationsToGroup, annotationIdentifiers],
  );

  if (groups.length === 0) return null;

  return (
    <ContextMenuSubmenu icon={FolderIcon} label={<T _str="move to group" swc />}>
      {groups.map((group) => {
        const Icon = GROUP_ICONS_BY_TYPE[group.type] ?? FolderIcon;
        return (
          <SelectItemContent key={group.identifier} icon={Icon} onClick={() => onMoveToGroup(group.identifier)}>
            {group.name}
          </SelectItemContent>
        );
      })}
    </ContextMenuSubmenu>
  );
};

export const MoveToGroupSubmenu = memo(MoveToGroupSubmenu_);
